'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { without, isEmpty } from 'underscore';
import { FolderPropTypes } from 'DesignData/propTypes/finder';
import { getIsInLockedFolderFactory } from 'DesignData/selectors/finder';
import { getTab, getTabsById, makeGetTabAsset, makeGetIsTabPinned } from 'DesignData/selectors/tabs';
import { closeTab as closeTabAction, closeTabs as closeTabsAction, updateTab as updateTabAction, openTab as openTabAction, moveTab as moveTabAction } from 'DesignData/actions/Tabs';
import { removePinnedTab as removePinnedTabAction } from 'DesignData/actions/Application';
import { TEMPLATE, LAYOUT, LAYOUT_SOURCE, GLOBAL_GROUP, CUSTOM_WIDGET, FOLDER } from 'DesignData/enums/DesignAssetTypes';
import { createPathFromAsset, createPath, createLayoutSourcePath } from 'DesignData/helpers/routingHelper';
import ContextMenuItemGroup from 'DesignData/models/ContextMenuItemGroup';
import ContextMenuItem from 'DesignData/models/ContextMenuItem';
import { byName as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import DesignAssetTypeIcons from 'DesignData/enums/DesignAssetTypeIcons';
import { getIsFolderOpen, makeGetAssetParentThemeFolderId } from 'DesignData/selectors/finder';
import { openDeeplyNestedFolder } from 'DesignData/actions/finder/Folders';
import { revealInFinder as revealInFinderAction } from 'DesignData/actions/finder/Selections';
import { DEFAULT_CSS_ICON, DEFAULT_JS_ICON, DEFAULT_TEMPLATE_ICON, DEFAULT_PARTIAL_ICON, DEFAULT_GLOBAL_GROUP_ICON, DEFAULT_LAYOUT_ICON } from 'DesignData/constants/Icons';
import { getIconNameFromDesignAssetType } from 'DesignData/lib/icon';
import { createOptimizedPick } from 'DesignData/lib/object';
import FormattedMessage from 'I18n/components/FormattedMessage';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import { setUserWelcomeTabStatus } from 'DesignData/actions/Onboarding';
import { getHasSeenWelcomeTab } from 'DesignData/selectors/onboarding';
const getAssetParentThemeFolderId = makeGetAssetParentThemeFolderId(); // Picks whitelisted render props

const pickRenderProps = createOptimizedPick(['tabId', 'iconName', 'pathName', 'tabsOrder', 'moveTab', 'makeTabActive', 'isActive', 'folderPath', 'hasBufferedChanges', 'label', 'isLocked', 'isPinned', 'use', 'assetParentThemeFolderId'], true);

const i18nKey = key => `designManager.tabs.${key}`;

function _getIconName(tab, tabAsset) {
  const defaultAssetTypeIcon = tabAsset ? getIconNameFromDesignAssetType(tab.assetType, tabAsset.path) : DesignAssetTypeIcons[tab.assetType];

  switch (tab.assetType) {
    case TEMPLATE:
      {
        if (!tabAsset) return DEFAULT_TEMPLATE_ICON;

        switch (tabAsset.templateType) {
          case TemplateTypes.css.id:
            {
              return DEFAULT_CSS_ICON;
            }

          case TemplateTypes.js.id:
            {
              return DEFAULT_JS_ICON;
            }

          default:
            return tabAsset.isAvailableForNewContent ? DEFAULT_TEMPLATE_ICON : DEFAULT_PARTIAL_ICON;
        }
      }

    case LAYOUT_SOURCE:
      {
        return DEFAULT_TEMPLATE_ICON;
      }

    case LAYOUT:
      {
        return tabAsset && tabAsset.templateType === TemplateTypes.global_group.id ? DEFAULT_GLOBAL_GROUP_ICON : DEFAULT_LAYOUT_ICON;
      }

    default:
      return defaultAssetTypeIcon;
  }
}

function getFolderPath(assetType, tabAsset) {
  if (!tabAsset) return null;

  switch (assetType) {
    case LAYOUT:
    case LAYOUT_SOURCE:
    case GLOBAL_GROUP:
      {
        return `${tabAsset.folder}/${tabAsset.label.split(' ').join('_')}`;
      }

    case TEMPLATE:
      {
        return tabAsset.path;
      }

    case CUSTOM_WIDGET:
      {
        /* TODO: Add folder path to custom widget buffer */
        return null;
      }

    default:
      return null;
  }
}

function getHasBufferedChanges(assetType, tabAsset) {
  if (!tabAsset) return false;

  switch (assetType) {
    case LAYOUT:
    case LAYOUT_SOURCE:
    case GLOBAL_GROUP:
    case TEMPLATE:
      {
        return !!tabAsset.meta.hasBufferedChanges;
      }

    case CUSTOM_WIDGET:
      {
        return !!tabAsset.isBuffered;
      }

    default:
      return false;
  }
}

function getPathName(tab, tabAsset) {
  if (tabAsset) {
    const path = createPathFromAsset(tabAsset);
    if (path) return path;
  }

  if (tab.assetType === TEMPLATE) {
    if (tabAsset && tabAsset.generatedFromLayoutId) {
      return createLayoutSourcePath(tabAsset.generatedFromLayoutId);
    }
  }

  return createPath(tab.assetType, tab.id, tab.editorOptions);
}

function makeMapStateToProps() {
  const getTabAsset = makeGetTabAsset();
  return function mapStateToProps(state, props) {
    const tab = getTab(state, props) || {};
    const {
      assetType
    } = tab;
    const tabAsset = getTabAsset(state, {
      assetId: tab.id,
      assetType
    });
    const getIsTabPinned = makeGetIsTabPinned(state);
    const getIsParentFolderOpen = getIsFolderOpen;
    const isParentFolderOpen = tabAsset ? getIsParentFolderOpen(state, {
      folderId: tabAsset.folderId
    }) : null;
    const getIsInLockedFolder = getIsInLockedFolderFactory(state);
    const folderId = assetType === FOLDER ? (tabAsset || {}).id : (tabAsset || {}).folderId;
    const {
      activeId,
      tabId
    } = props;
    return {
      tab,
      tabs: getTabsById(state),
      iconName: _getIconName(tab, tabAsset),
      pathName: getPathName(tab, tabAsset),
      isActive: tab.id === activeId,
      folderPath: getFolderPath(assetType, tabAsset),
      hasBufferedChanges: getHasBufferedChanges(assetType, tabAsset),
      folderId,
      isParentFolderOpen,
      hasSeenWelcomeTab: getHasSeenWelcomeTab(state),
      isLocked: getIsInLockedFolder(folderId),
      isPinned: getIsTabPinned(tabId),
      assetParentThemeFolderId: getAssetParentThemeFolderId(state, {
        assetId: tab.id
      })
    };
  };
}

const mapDispatchToProps = {
  closeTab: closeTabAction,
  closeTabs: closeTabsAction,
  updateTab: updateTabAction,
  moveTab: moveTabAction,
  openTab: openTabAction,
  openFolder: openDeeplyNestedFolder,
  removePinnedTab: removePinnedTabAction,
  revealInFinder: revealInFinderAction,
  setUserHasSeenWelcomeTab: setUserWelcomeTabStatus(true)
};
export default function withConnectedTab(WrappedComponent) {
  class HighOrderConnectedTab extends Component {
    constructor(...args) {
      super(...args);

      this.closeOtherTabs = () => {
        const {
          tabsOrder,
          tab,
          closeTabs,
          tabs
        } = this.props;
        const tabIds = without(tabsOrder, tab.id);
        const tabsToClose = tabIds.map(tabId => tabs[tabId]);
        closeTabs(tabsToClose);
      };

      this.closeTabsToRight = () => {
        const {
          tabsOrder,
          tab,
          closeTabs,
          tabs
        } = this.props;
        const tabIds = tabsOrder.slice(tabsOrder.indexOf(tab.id) + 1);
        const tabsToClose = tabIds.map(tabId => tabs[tabId]);
        closeTabs(tabsToClose);
      };

      this.closeAllTabs = () => this.props.closeTabs(this.props.tabsOrder);

      this.revealInFinder = () => {
        const {
          folderId,
          openFolder,
          tab,
          revealInFinder,
          isParentFolderOpen
        } = this.props;

        if (!isParentFolderOpen) {
          openFolder(folderId);
        }

        revealInFinder(tab.id);
      };

      this.onClose = e => {
        const {
          closeTab,
          hasSeenWelcomeTab,
          tab
        } = this.props;

        if ((tab.id === 'welcome' || tab.id === 'gettingStarted') && !hasSeenWelcomeTab) {
          this.props.setUserHasSeenWelcomeTab();
        }

        e.preventDefault();
        e.stopPropagation();
        closeTab(tab);
      };

      this.onContextMenuClick = e => {
        const menuItems = this.createContextMenuItems();
        this.props.openContextMenu(e, menuItems);
      };

      this.onPinClick = e => {
        const {
          tab,
          removePinnedTab,
          updateTab
        } = this.props;
        e.preventDefault();
        e.stopPropagation();
        removePinnedTab(tab.id);
        updateTab(tab, {
          isPinned: false
        });
      };

      this.makeTabActive = () => {
        const {
          openTab,
          tab
        } = this.props;
        openTab(tab);
      };
    }

    shouldComponentUpdate(nextProps) {
      // // Use this to determine props causing re-renders.
      // const diff = _.omit(this.props, (value, key) => nextProps[key] === value);
      // if (Object.keys(diff).length) {
      //   console.log('DIFF %s --> %o', nextProps.tab.id, diff);
      // }

      /* Stop bulk delete from trying to refresh deleted tab */
      if (isEmpty(nextProps.tab)) return false;
      return true;
    }

    createContextMenuItems() {
      const {
        closeTab,
        tab,
        tabsOrder,
        folderId
      } = this.props;
      const options = [['CLOSE_ALL_TABS', 'CLOSE_OTHER_TABS', 'CLOSE_TAB', 'CLOSE_TABS_TO_RIGHT', 'REVEAL_IN_FINDER']];
      const callbacks = {
        CLOSE_TAB: () => closeTab(tab),
        CLOSE_ALL_TABS: this.closeAllTabs,
        CLOSE_OTHER_TABS: this.closeOtherTabs,
        CLOSE_TABS_TO_RIGHT: this.closeTabsToRight,
        REVEAL_IN_FINDER: this.revealInFinder
      };
      const labels = {
        CLOSE_TAB: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('closeTabLabel')
        }),
        CLOSE_ALL_TABS: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('closeAllTabsLabel')
        }),
        CLOSE_OTHER_TABS: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('closeOtherTabsLabel')
        }),
        CLOSE_TABS_TO_RIGHT: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('closeTabsToRightLabel')
        }),
        REVEAL_IN_FINDER: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('revealLabel')
        })
      };
      const disabled = {
        CLOSE_TABS_TO_RIGHT: tabsOrder.indexOf(tab.id) === tabsOrder.length - 1,
        REVEAL_IN_FINDER: !folderId
      };
      return options.map(actionsList => {
        const menuActions = actionsList.map(option => {
          return new ContextMenuItem({
            label: labels[option],
            callback: callbacks[option],
            disabled: disabled[option],
            key: option
          });
        });
        return new ContextMenuItemGroup({
          options: menuActions
        });
      });
    }

    render() {
      const props = pickRenderProps(this.props);
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
        onClose: this.onClose,
        onPinClick: this.onPinClick,
        onContextMenu: this.onContextMenuClick,
        makeTabActive: this.makeTabActive
      }));
    }

  }

  HighOrderConnectedTab.propTypes = {
    tab: PropTypes.object,
    tabs: PropTypes.object.isRequired,
    iconName: PropTypes.string,
    pathName: PropTypes.string,
    closeTab: PropTypes.func.isRequired,
    closeTabs: PropTypes.func.isRequired,
    updateTab: PropTypes.func.isRequired,
    moveTab: PropTypes.func.isRequired,
    openTab: PropTypes.func.isRequired,
    openContextMenu: PropTypes.func.isRequired,
    tabsOrder: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
    folderPath: PropTypes.string,
    hasBufferedChanges: PropTypes.bool,
    label: PropTypes.node,
    folderId: FolderPropTypes.folderId,
    isParentFolderOpen: PropTypes.bool,
    openFolder: PropTypes.func.isRequired,
    removePinnedTab: PropTypes.func.isRequired,
    revealInFinder: PropTypes.func.isRequired,
    isActive: PropTypes.bool,
    setUserHasSeenWelcomeTab: PropTypes.func.isRequired,
    hasSeenWelcomeTab: PropTypes.bool
  };
  HighOrderConnectedTab.contextType = TourContext;
  return connect(makeMapStateToProps, mapDispatchToProps)(HighOrderConnectedTab);
}